import { PenIcon } from "@xola/icons";
import { getSymbol, Label, Table } from "@xola/ui-kit";
import { DemographicIcon } from "common";
import { isNil, round, sortBy } from "lodash";
import React from "react";
import { Experience, Package, PurchaseRule, PurchaseRuleShortcutType } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useVisibility } from "../../../hooks/useVisibility";
import { getPackageDemographicCatalog } from "../../../models/Package";
import { ProductImage } from "../../Common/ProductImage";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { EditDemographicPricingModal } from "./EditDemographicPricingModal";
import { DiscountPricingValue, getDefaultAction, getPurchaseAction, getShortcutType } from "./PricingSelectorHelper";

interface PackageExperienceDemographicDiscountListProps {
    pkg: Package;
    purchaseRule?: PurchaseRule;
    experience: Experience;
    onChangeDemographicDiscount: (addonPricingValue: DiscountPricingValue[]) => void;
}

export const PackageExperienceDemographicDiscountList = ({
    purchaseRule,
    pkg,
    experience,
    onChangeDemographicDiscount,
}: PackageExperienceDemographicDiscountListProps) => {
    const [visible, { toggle }] = useVisibility(false);
    const seller = useSeller();
    const symbol = getSymbol(seller.currency);
    // Pass all demographics after overlapping demographics removed
    const packageDemographics = getPackageDemographicCatalog(pkg, experience.id);

    const handleDiscountChange = (addonPricingValue: DiscountPricingValue[]) => {
        onChangeDemographicDiscount(addonPricingValue);
        toggle();
    };

    if (!purchaseRule) {
        return null;
    }

    return (
        <div className="pb-4">
            <div className="flex items-center  justify-between  pb-1">
                <div className="mb-0.5 flex items-center space-x-2">
                    <ProductImage product={experience} size="small" />
                    <Label>{experience.name}</Label>
                </div>
                <SecondaryButton className="mb-1" size="tiny" onClick={toggle}>
                    <PenIcon />
                </SecondaryButton>

                <EditDemographicPricingModal
                    isOpen={visible}
                    demographics={packageDemographics}
                    experience={experience}
                    package={pkg}
                    purchaseRule={purchaseRule}
                    onClose={toggle}
                    onChange={handleDiscountChange}
                />
            </div>

            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header className="!text-sm">Demographics</Table.Header>
                        <Table.Header className="!text-sm">Adjustment</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped={false}>
                    {sortBy(packageDemographics, "sequence").map((demographic) => {
                        const purchaseAction = getPurchaseAction(purchaseRule, demographic.template?.id);
                        const action = purchaseAction ?? getDefaultAction();
                        const discount = Math.abs(round(action.shortcut?.amount ?? 0, 2));
                        const shortcutType = getShortcutType(action);
                        const label =
                            shortcutType === PurchaseRuleShortcutType.PERCENT
                                ? `${discount}% Off`
                                : shortcutType === PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE
                                ? `${symbol}${discount}`
                                : `${symbol}${discount} Off`;

                        return (
                            <Table.Row key={demographic.id} className="justify-between">
                                <Table.Cell className="w-2/3">
                                    <span className="whitespace-normal break-all">
                                        <DemographicIcon className="mr-1" name={demographic.name} />
                                        {demographic.name}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>{isNil(discount) ? null : label}</Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
        </div>
    );
};
